<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import HeaderComponent from '@/components/HeaderComponent.vue';
import FooterComponent from '@/components/FooterComponent.vue';
import apiClient from '@/services/api'
import { toast } from 'vue3-toastify'
import { useRoute, useRouter } from 'vue-router';

const route = useRoute();
const router = useRouter();

let searchDebounceTimer = null;

const debouncedApplyFilters = () => {
  clearTimeout(searchDebounceTimer);
  searchDebounceTimer = setTimeout(applyFilters, 350);
};

const applyFiltersNow = () => {
  clearTimeout(searchDebounceTimer);
  applyFilters();
};

// Data
const apartments = ref([]);
const loading = ref(false);
const currentPage = ref(1);
const itemsPerPage = ref(12);
const totalPages = ref(1);

// Modal popup
const showCategoryModal = ref(false);

// Filters
const filters = ref({
  keyword: '',
  locality: '',
  type: '',
  rooms: '',
  budget: '',
  startDate: '',
  endDate: '',
  country: route.query.country || '',
  category: ''
});

// Dropdown filters
const isOpen = ref(false);
const selectedFilters = ref([]);
const filterOptions = [
  { value: 'wifi', label: 'Wi-Fi' },
  { value: 'security', label: 'Sécurité' },
  { value: 'waterHeater', label: 'Chauffe-eau' },
  { value: 'terrace', label: 'Terrasse' },
  { value: 'privateEntrance', label: 'Entrée privée' }
];

const getPageFromRoute = () => {
  const page = Number(route.query.page);
  return Number.isInteger(page) && page > 0 ? page : 1;
};

const syncPageInUrl = (page) => {
  const query = { ...route.query };
  if (page > 1) {
    query.page = String(page);
  } else {
    delete query.page;
  }
  router.push({ query });
};

const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
}

const handleClickOutside = (event) => {
  if (!event.target.closest('.filter-dropdown')) {
    isOpen.value = false;
  }
};

// Computed
const selectedLabels = computed(() =>
  filterOptions.filter(opt => selectedFilters.value.includes(opt.value)).map(opt => opt.label)
);

// Pagination range - Affiche: première, avant, après, dernière
const paginationRange = computed(() => {
  const pages = new Set();
  
  // Toujours afficher la première
  pages.add(1);
  
  // Afficher avant et après la page actuelle
  if (currentPage.value > 1) {
    pages.add(currentPage.value - 1);
  }
  pages.add(currentPage.value);
  if (currentPage.value < totalPages.value) {
    pages.add(currentPage.value + 1);
  }
  
  // Toujours afficher la dernière
  if (totalPages.value > 1) {
    pages.add(totalPages.value);
  }
  
  // Convertir en tableau et trier
  const pagesArray = Array.from(pages).sort((a, b) => a - b);
  
  // Ajouter "..." pour les gaps
  const result = [];
  pagesArray.forEach((page, index) => {
    if (index > 0 && pagesArray[index - 1] < page - 1) {
      result.push({ type: 'dot' });
    }
    result.push({ type: 'page', page });
  });
  
  return result;
});

// Methods
const selectCategory = (category) => {
  filters.value.category = category;
  showCategoryModal.value = false;
  applyFilters();
};

const showAllApartments = () => {
  filters.value.category = '';
  showCategoryModal.value = false;
  applyFilters();
};

const fetchApartments = async () => {
  try {
    loading.value = true;
    
    const params = {
      page: currentPage.value,
      limit: itemsPerPage.value,
      ...filters.value
    };
    
    // Nettoyer les paramètres vides
    Object.keys(params).forEach(key => {
      if (params[key] === '' || params[key] === null || params[key] === undefined) {
        delete params[key];
      }
    });
    
    // Convertir les nombres
    if (params.budget) params.budget = Number(params.budget);
    if (params.rooms) params.rooms = Number(params.rooms);
    
    // Ajouter les filtres sélectionnés
    selectedFilters.value.forEach(filter => {
      params[filter] = true;
    });

    const { data } = await apiClient.get('/apartments', { params });
    
    apartments.value = data.data;
    totalPages.value = data.pagination.totalPages;
    
  } catch (error) {
    toast.error('Aucun résultat trouvé', {
      position: 'top-right',
      autoClose: 3000
    });
  } finally {
    loading.value = false;
  }
};

const applyFilters = () => {
  currentPage.value = 1;
  syncPageInUrl(1);
  fetchApartments();
};

const changePage = (page) => {
  if (page < 1 || page > totalPages.value || page === currentPage.value) return;
  currentPage.value = page;
  syncPageInUrl(page);
  fetchApartments();
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const formatPrice = (price) => {
  return new Intl.NumberFormat('fr-FR').format(price);
};

// Fonction pour déterminer si un appartement est meublé
const isFurnished = (apartment) => {
  const amenities = [
    apartment.wifi,
    apartment.security,
    apartment.waterHeater,
    apartment.terrace,
    apartment.privateEntrance,
    apartment.smokerFriendly,
    apartment.tv,
    apartment.jardin,
    apartment.piscine,
    apartment.generator,
    apartment.washingMachine,
    apartment.iron,
    apartment.airConditioner,
    apartment.fan,
    apartment.extra?.microwave,
    apartment.extra?.canal,
    apartment.extra?.netflix
  ];
  
  // Compte le nombre d'équipements disponibles (true)
  const availableAmenities = amenities.filter(item => item === true).length;
  
  // Si au moins 3 équipements sont disponibles, considérer comme meublé
  return availableAmenities >= 3;
};

const shareApartment = async (apartment) => {
  const shareUrl = `${window.location.origin}/apartments/${apartment._id}`
  const shareText = `Découvrez ce bien à ${apartment.locality}: ${apartment.title}`

  try {
    if (navigator.share) {
      await navigator.share({
        title: apartment.title,
        text: shareText,
        url: shareUrl
      })
      return
    }

    if (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      window.open(`whatsapp://send?text=${encodeURIComponent(shareText + '\n' + shareUrl)}`, '_blank')
    } 
    else if (navigator.clipboard) {
      await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`)
      toast.success('Lien copié dans le presse-papier !', {
        position: 'top-right',
        autoClose: 3000
      })
    } else {
      copyToClipboardFallback(`${shareText}\n${shareUrl}`)
      toast.success('Lien copié dans le presse-papier !', {
        position: 'top-right',
        autoClose: 3000
      })
    }
  } catch (err) {
    console.error('Erreur de partage:', err)
    toast.error('Échec du partage', {
      position: 'top-right',
      autoClose: 3000
    })
  }
}

const copyToClipboardFallback = (text) => {
  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.style.position = 'fixed'
  document.body.appendChild(textarea)
  textarea.select()
  document.execCommand('copy')
  document.body.removeChild(textarea)
}

// Lifecycle
onMounted(() => {
  currentPage.value = getPageFromRoute();
  // Afficher le modal si country est présent dans l'URL et category n'est pas définie
  if (route.query.country && !route.query.category) {
    showCategoryModal.value = true;
  } else {
    fetchApartments();
  }
  document.addEventListener('click', handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
  <div>
    <HeaderComponent/>
  
  <!-- Modal de sélection de catégorie -->
  <div 
    v-if="showCategoryModal" 
    class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
    @click.self="showCategoryModal = false"
  >
    <div class="bg-white rounded-2xl max-w-md w-full p-8 shadow-2xl transform transition-all">
      <div class="text-center mb-6">
        <div class="inline-flex items-center justify-center w-16 h-16 bg-[#D4AF37] rounded-full mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" class="text-white">
            <path fill="currentColor" fill-rule="evenodd" d="M13.106 22h-2.212c-3.447 0-5.17 0-6.345-1.012s-1.419-2.705-1.906-6.093l-.279-1.937c-.38-2.637-.57-3.956-.029-5.083s1.691-1.813 3.992-3.183l1.385-.825C9.8 2.622 10.846 2 12 2s2.199.622 4.288 1.867l1.385.825c2.3 1.37 3.451 2.056 3.992 3.183s.35 2.446-.03 5.083l-.278 1.937c-.487 3.388-.731 5.081-1.906 6.093S16.553 22 13.106 22m-4.708-6.447a.75.75 0 0 1 1.049-.156c.728.54 1.607.853 2.553.853s1.825-.313 2.553-.853a.75.75 0 1 1 .894 1.205A5.77 5.77 0 0 1 12 17.75a5.77 5.77 0 0 1-3.447-1.148a.75.75 0 0 1-.155-1.049" clip-rule="evenodd"/>
          </svg>
        </div>
        <h2 class="text-2xl font-bold text-gray-800 mb-2">
          Que recherchez-vous ?
        </h2>
        <p class="text-gray-600">
          Sélectionnez le type de bien qui vous intéresse au <span class="font-semibold text-[#1f2937]">{{ filters.country }}</span>
        </p>
      </div>

      <div class="space-y-3">
        <button 
          @click="selectCategory('achat')"
          class="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 border-2 border-gray-200 hover:border-[#D4AF37] rounded-xl transition-all duration-200 group"
        >
          <div class="flex items-center gap-3">
            <div class="w-12 h-12 bg-[#1f2937] rounded-lg flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" class="text-white">
                <path fill="currentColor" d="M5 21q-.825 0-1.412-.587T3 19V5q0-.825.588-1.412T5 3h14q.825 0 1.413.588T21 5v14q0 .825-.587 1.413T19 21zm7-9.5q-1.05 0-1.775-.725T9.5 9t.725-1.775T12 6.5t1.775.725T14.5 9t-.725 1.775T12 11.5M5 19h14v-1.15q-1.35-1.325-3.138-2.087T12 15t-3.862.763T5 17.85z"/>
              </svg>
            </div>
            <div class="text-left">
              <p class="font-bold text-gray-800">Biens en vente</p>
              <p class="text-sm text-gray-600">Acheter une propriété</p>
            </div>
          </div>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" class="text-[#1f2937] group-hover:translate-x-1 transition-transform">
            <path fill="currentColor" d="M8.59 16.59L13.17 12L8.59 7.41L10 6l6 6l-6 6z"/>
          </svg>
        </button>

        <button 
          @click="selectCategory('reservation')"
          class="w-full flex items-center justify-between p-4 bg-yellow-50 hover:bg-yellow-100 border-2 border-yellow-200 hover:border-yellow-400 rounded-xl transition-all duration-200 group"
        >
          <div class="flex items-center gap-3">
            <div class="w-12 h-12 bg-[#D4AF37] rounded-lg flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" class="text-white">
                <path fill="currentColor" d="M9 11H7v2h2zm4 0h-2v2h2zm4 0h-2v2h2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2m0 16H5V9h14z"/>
              </svg>
            </div>
            <div class="text-left">
              <p class="font-bold text-gray-800">Biens en location</p>
              <p class="text-sm text-gray-600">Réserver un logement</p>
            </div>
          </div>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" class="text-[#D4AF37] group-hover:translate-x-1 transition-transform">
            <path fill="currentColor" d="M8.59 16.59L13.17 12L8.59 7.41L10 6l6 6l-6 6z"/>
          </svg>
        </button>

        <button 
          @click="showAllApartments"
          class="w-full p-4 bg-gray-100 hover:bg-gray-200 border-2 border-gray-300 hover:border-gray-400 rounded-xl transition-all duration-200 text-gray-700 font-semibold"
        >
          Voir tous les biens
        </button>
      </div>

      <button 
        @click="showCategoryModal = false"
        class="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" class="text-gray-600">
          <path fill="currentColor" d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12z"/>
        </svg>
      </button>
    </div>
  </div>

  <section class="w-full relative flex items-center justify-center h-[350px] py-[50px] pt-[130px]">
    <div class="absolute top-0 left-0 w-full h-full overflow-hidden">
      <img src="../assets/images/photo_about3.avif" alt="" class="w-full h-full absolute object-cover object-center">
      <div class="degradeB"></div>
    </div>
    <div class="container lg:max-w-[1200px] flex md:flex-row flex-col w-full justify-center items-center relative px-[20px]">
      <div class="w-full mb-[-220px] flex-col flex items-center justify-center relative">
        <div class="flex items-center justify-start w-full">
          <button 
            class="px-[20px] py-[10px] md:px-[30px] md:py-[15px] text-white bg-gray-800 rounded-t-[10px]"
            @click="filterType = 'rent'"
          >
            Réservations
          </button>
        </div>
        <div class="w-full flex items-center flex-wrap md:flex-nowrap justify-center relative">
          <div class="w-full bg-white flex p-[10px] flex-wrap md:rounded-bl-[10px] rounded-tr-[10px] border border-gray-200 ">
            <div class="md:w-[15%] w-[50%] relative">
              <label for="keyword" class="absolute top-[10px] left-[10px] text-sm font-bold text-gray-800">Mots Clés</label>
              <input
                type="text"
                id="keyword"
                v-model="filters.keyword"
                class="w-full outline-none text-sm p-[10px] pt-[40px] bg-transparent border-r"
                placeholder="Recherchez une propriété"
                @input="debouncedApplyFilters"
                @keyup.enter="applyFiltersNow"
              >
            </div>
            <div class="md:w-[20%] w-[50%] relative">
              <label for="locality" class="absolute top-[10px] left-[10px] text-sm font-bold text-gray-800">Localisation</label>
              <input
                type="text"
                id="locality"
                v-model="filters.locality"
                class="w-full outline-none text-sm p-[10px] pt-[40px] bg-transparent pr-[35px] md:border-r"
                placeholder="Recherchez une localisation"
                @input="debouncedApplyFilters"
                @keyup.enter="applyFiltersNow"
              >
              <span class="absolute bottom-[10px] right-[10px] text-gray-600">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M11 22.95v-2q-3.125-.35-5.363-2.587T3.05 13h-2v-2h2q.35-3.125 2.588-5.363T11 3.05v-2h2v2q3.125.35 5.363 2.588T20.95 11h2v2h-2q-.35 3.125-2.587 5.363T13 20.95v2zM12 19q2.9 0 4.95-2.05T19 12t-2.05-4.95T12 5T7.05 7.05T5 12t2.05 4.95T12 19m0-3q-1.65 0-2.825-1.175T8 12t1.175-2.825T12 8t2.825 1.175T16 12t-1.175 2.825T12 16"/>
                </svg>
              </span>
            </div>
            <div class="md:w-[20%] w-[50%] relative">
              <label for="type" class="absolute top-[10px] left-[10px] text-sm font-bold text-gray-800">Type de Biens</label>
              <select 
                v-model="filters.type" 
                class="w-full outline-none text-sm p-[10px] h-[70px] bg-transparent pt-[40px] border-r rounded-[5px]"
                @change="applyFilters"
              >
                <option value="">Tous les types</option>
                <option value="Appartement">Appartement</option>
                <option value="Studio">Studio</option>
                <option value="Maison">Maison</option>
                <option value="Villa">Villa</option>
              </select>
            </div>
            <div class="md:w-[15%] w-[50%] relative">
              <label for="budget" class="absolute top-[10px] left-[10px] text-sm font-bold text-gray-800">Budget max</label>
              <input
                type="number"
                id="budget"
                v-model="filters.budget"
                class="w-full outline-none text-sm p-[10px] pt-[40px] bg-transparent"
                placeholder="Budget maximum"
                @input="debouncedApplyFilters"
                @keyup.enter="applyFiltersNow"
              >
            </div>
            <div class="md:w-[15%] w-[50%] relative">
              <label for="dateStart" class="absolute top-[10px] left-[10px] text-sm font-bold text-gray-800">Date de début</label>
              <input 
                type="date" 
                id="dateStart"
                v-model="filters.startDate"
                class="w-full outline-none text-sm p-[10px] pt-[40px] bg-transparent"
                @change="applyFilters"
              >
            </div>
            <div class="md:w-[15%] w-[50%] relative">
              <label for="dateEnd" class="absolute top-[10px] left-[10px] text-sm font-bold text-gray-800">Date de Départ</label>
              <input 
                type="date" 
                id="dateEnd"
                v-model="filters.endDate"
                class="w-full outline-none text-sm p-[10px] pt-[40px] bg-transparent"
                @change="applyFilters"
              >
            </div>
          </div>
        </div>
        <div class="flex items-center justify-end w-full">
          <button 
            class="md:w-[150px] w-full p-[15px] bg-gray-800 text-white md:rounded-b-[10px] rounded-b-[10px] flex items-center justify-center hover:bg-[#D4AF37] duration-300"
            @click="applyFilters"
          >
            Trouver un bien
          </button>
        </div>
      </div>
    </div>
  </section>

  <section class="w-full py-[50px] md:mt-[50px] mt-[85px] flex items-center justify-center relative">
    <div class="w-full container lg:max-w-[1200px] flex flex-col items-center justify-center relative px-[20px]">
      <div class="w-full mt-[50px] mb-[25px] flex items-center justify-between relative flex-wrap">
        <h2 class="text-[#1f2937] text-2xl md:text-3xl font-bold">
          Nos Biens
          <span v-if="filters.country" class="text-lg text-gray-600"> - {{ filters.country }}</span>
        </h2>
        <div class="flex items-center relative gap-[20px]">
          <select 
            v-model="itemsPerPage" 
            @change="applyFilters"
            class="w-[150px] h-[40px] bg-white border border-gray-200 rounded-[5px] text-sm font-semibold text-gray-800 outline-none px-[10px]"
          >
            <option value="12">12 par page</option>
            <option value="24">24 par page</option>
            <option value="48">48 par page</option>
          </select>
          <div class="relative inline-block w-[calc(50%-10px)] md:block md:w-[250px] filter-dropdown">
            <div @click.stop="toggleDropdown" class="border rounded p-[10px] bg-white cursor-pointer flex justify-between items-center">
              <span class="text-gray-600 md:text-sm text-xs font-semibold">
                {{ selectedLabels.length ? selectedLabels.join(', ') : 'Filtrer par' }}
              </span>
              <svg class="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
              </svg>
            </div>
          
            <div v-if="isOpen" class="absolute z-10 w-full bg-white border mt-1 rounded shadow-md max-h-60 overflow-y-auto">
              <div v-for="option in filterOptions" :key="option.value" class="px-4 py-2 hover:bg-gray-100 flex items-center gap-2">
                <input
                  type="checkbox"
                  :id="option.value"
                  :value="option.value"
                  v-model="selectedFilters"
                  class="form-checkbox text-[#1f2937]"
                  @change="applyFilters"
                />
                <label :for="option.value" class="cursor-pointer">{{ option.label }}</label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="loading && apartments.length === 0" class="w-full flex items-center justify-center py-20">
        <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4AF37]"></div>
      </div>

      <div v-else>
        <div v-if="apartments.length === 0" class="w-full flex flex-col items-center justify-center py-20">
          <svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 24 24" class="text-gray-400 mb-4">
            <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10s10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
          </svg>
          <h3 class="text-xl font-semibold text-gray-600">Aucun résultat trouvé</h3>
          <p class="text-gray-500 mt-2">Essayez de modifier vos critères de recherche</p>
        </div>

        <div v-else class="w-full grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-6">
          <!-- 
          <div 
            v-for="apartment in apartments" 
            :key="apartment._id"
            class="bg-white rounded-xl shadow-4xl relative overflow-hidden hover:shadow-lg transition-shadow duration-300"
          >
            <button 
              @click="shareApartment(apartment)"
              class="w-[30px] h-[30px] rounded-full absolute top-[10px] left-[10px] z-10 flex items-center justify-center text-white bg-black/50 hover:bg-black/70 transition-colors duration-200 group"
              title="Partager ce bien"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" class="group-hover:scale-110 transition-transform">
                <circle cx="18" cy="5" r="1" fill="currentColor" opacity=".3"/>
                <circle cx="6" cy="12" r="1" fill="currentColor" opacity=".3"/>
                <circle cx="18" cy="19.02" r="1" fill="currentColor" opacity=".3"/>
                <path fill="currentColor" d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81c1.66 0 3-1.34 3-3s-1.34-3-3-3s-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65c0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92M18 4c.55 0 1 .45 1 1s-.45 1-1 1s-1-.45-1-1s.45-1 1-1M6 13c-.55 0-1-.45-1-1s.45-1 1-1s1 .45 1 1s-.45 1-1 1m12 7.02c-.55 0-1-.45-1-1s.45-1 1-1s1 .45 1 1s-.45 1-1 1"/>
              </svg>
            </button>
            <div class="relative h-48 overflow-hidden">
              <img 
                :src="'https://api.mohimmo.com/uploads/'+apartment.images[0] || 'https://placehold.co/600x400?text=No+Image'" 
                :alt="apartment.title"
                class="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              >
              <div class="absolute top-3 right-3 bg-[#D4AF37] text-white text-xs font-bold px-2 py-1 rounded">
                À Réserver
              </div>
            </div>
            <div class="p-4">
              <h3 class="text-lg font-bold text-gray-800 mb-2 flex flex-wrap items-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" class="mr-2">
                  <path fill="currentColor" fill-rule="evenodd" d="M13.106 22h-2.212c-3.447 0-5.17 0-6.345-1.012s-1.419-2.705-1.906-6.093l-.279-1.937c-.38-2.637-.57-3.956-.029-5.083s1.691-1.813 3.992-3.183l1.385-.825C9.8 2.622 10.846 2 12 2s2.199.622 4.288 1.867l1.385.825c2.3 1.37 3.451 2.056 3.992 3.183s.35 2.446-.03 5.083l-.278 1.937c-.487 3.388-.731 5.081-1.906 6.093S16.553 22 13.106 22m-4.708-6.447a.75.75 0 0 1 1.049-.156c.728.54 1.607.853 2.553.853s1.825-.313 2.553-.853a.75.75 0 1 1 .894 1.205A5.77 5.77 0 0 1 12 17.75a5.77 5.77 0 0 1-3.447-1.148a.75.75 0 0 1-.155-1.049" clip-rule="evenodd"/>
                </svg>
                {{ apartment.title }} <span class="text-xs w-full text-gray-500"> {{apartment.locality}} </span>
              </h3>
              
              <div class="flex flex-wrap gap-3 mb-3 text-sm text-gray-600">
                <span class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" class="mr-1">
                    <g fill="none" fill-rule="evenodd">
                      <path d="m12.594 23.258l-.012.002l-.071.035l-.02.004l-.014-.004l-.071-.036q-.016-.004-.024.006l-.004.01l-.017.428l.005.02l.01.013l.104.074l.015.004l.012-.004l.104-.074l.012-.016l.004-.017l-.017-.427q-.004-.016-.016-.018m.264-.113l-.014.002l-.184.093l-.01.01l-.003.011l.018.43l.005.012l.008.008l.201.092q.019.005.029-.008l.004-.014l-.034-.614q-.005-.019-.02-.022m-.715.002a.02.02 0 0 0-.027.006l-.006.014l-.034.614q.001.018.017.024l.015-.002l.201-.093l.01-.008l.003-.011l.018-.43l-.003-.012l-.01-.01z"/>
                      <path fill="currentColor" d="M3 5a1 1 0 0 1 .993.883L4 6v7h7V8a1 1 0 0 1 .883-.993L12 7h7a3 3 0 0 1 2.995 2.824L22 10v8a1 1 0 0 1-1.993.117L20 18v-3H4v3a1 1 0 0 1-1.993.117L2 18V6a1 1 0 0 1 1-1m4.5 2a2.5 2.5 0 1 1 0 5a2.5 2.5 0 0 1 0-5"/>
                    </g>
                  </svg>
                  {{ apartment.rooms }} Chambres
                </span>
                <span class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" class="mr-1">
                    <path fill="currentColor" d="M6 22q-.825 0-1.412-.587T4 20V4q0-.825.588-1.412T6 2h12q.825 0 1.413.588T20 4v16q0 .825-.587 1.413T18 22zm0-2h12V4H6zm1-2h10l-3.45-4.5l-2.3 3l-1.55-2zm-1 2V4z"/>
                  </svg>
                  {{ apartment.images.length }} Photos
                </span>
                <span class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" class="mr-1">
                    <path fill="currentColor" d="M10 5v5H9V5H5v8h4v-1h1v5H9v-3H5v5h7v-2h1v2h6v-2h2v4H3V3h18v12h-2v-5h-6v5h-1V9h7V5z"/>
                  </svg>
                  {{ apartment.floor || 'N/A' }}
                </span>
              </div>
              
              <div class="flex items-center justify-between mt-4">
                <span class="text-lg font-bold text-[#1f2937]">
                  {{ formatPrice(apartment.price) }} <span class="text-sm">F/nuitée</span>
                </span>
                <router-link 
                  :to="`/apartments/${apartment._id}`" 
                  class="px-4 py-2 bg-[#D4AF37] text-gray-800 rounded-md font-semibold hover:bg-[#c5a030] transition-colors text-sm"
                >
                  Voir détails
                </router-link>
              </div>
            </div>
          </div>
          -->
          <div 
            v-for="apartment in apartments" 
            :key="apartment._id"
            class="rounded-t-[26px] relative overflow-hidden duration-300"
          >
            <button 
              @click="shareApartment(apartment)"
              class="w-[30px] h-[30px] rounded-full absolute top-[10px] right-[10px] z-10 flex items-center justify-center text-white bg-black/50 hover:bg-black/70 transition-colors duration-200 group"
              title="Partager ce bien"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" class="group-hover:scale-110 transition-transform">
                <circle cx="18" cy="5" r="1" fill="currentColor" opacity=".3"/>
                <circle cx="6" cy="12" r="1" fill="currentColor" opacity=".3"/>
                <circle cx="18" cy="19.02" r="1" fill="currentColor" opacity=".3"/>
                <path fill="currentColor" d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81c1.66 0 3-1.34 3-3s-1.34-3-3-3s-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65c0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92M18 4c.55 0 1 .45 1 1s-.45 1-1 1s-1-.45-1-1s.45-1 1-1M6 13c-.55 0-1-.45-1-1s.45-1 1-1s1 .45 1 1s-.45 1-1 1m12 7.02c-.55 0-1-.45-1-1s.45-1 1-1s1 .45 1 1s-.45 1-1 1"/>
              </svg>
            </button>
            <div class="relative h-[220px] rounded-[26px] overflow-hidden">
              <img 
                :src="'https://api.mohimmo.com/uploads/'+apartment.images[0] || 'https://placehold.co/600x400?text=No+Image'" 
                :alt="apartment.title"
                class="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              >
              <div v-if="apartment?.category === 'achat'" class="absolute top-[10px] left-[10px] bg-[#1f2937] text-white text-xs font-bold px-2 py-2 rounded-[16px]">
                En vente
              </div>
              <div v-else class="absolute top-[10px] left-[10px] bg-[#D4AF37] text-white text-xs font-bold px-2 py-2 rounded-[16px]">
                À Réserver
              </div>
              <!-- Tag Meublé / Non meublé -->
              <div v-if="isFurnished(apartment)" class="absolute top-[50px] left-[10px] bg-green-600 text-white text-xs font-bold px-2 py-2 rounded-[16px] flex items-center gap-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M7 13c1.66 0 3-1.34 3-3S8.66 7 7 7s-3 1.34-3 3s1.34 3 3 3m12-6h-8v7H3V7H1v10h22v-6c0-2.21-1.79-4-4-4"/>
                </svg>
                Meublé
              </div>
              <div v-else class="absolute top-[50px] left-[10px] bg-gray-600 text-white text-xs font-bold px-2 py-2 rounded-[16px] flex items-center gap-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10s10-4.48 10-10S17.52 2 12 2m0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8s8 3.59 8 8s-3.59 8-8 8"/>
                </svg>
                Non meublé
              </div>
            </div>
            <div class="py-2">
              <router-link
                :to="`/apartments/${apartment._id}`" class="text-sm font-semibold cursor-pointer hover:underline duration-200 text-gray-800 mb-2 flex flex-wrap items-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" class="mr-2">
                  <path fill="currentColor" fill-rule="evenodd" d="M13.106 22h-2.212c-3.447 0-5.17 0-6.345-1.012s-1.419-2.705-1.906-6.093l-.279-1.937c-.38-2.637-.57-3.956-.029-5.083s1.691-1.813 3.992-3.183l1.385-.825C9.8 2.622 10.846 2 12 2s2.199.622 4.288 1.867l1.385.825c2.3 1.37 3.451 2.056 3.992 3.183s.35 2.446-.03 5.083l-.278 1.937c-.487 3.388-.731 5.081-1.906 6.093S16.553 22 13.106 22m-4.708-6.447a.75.75 0 0 1 1.049-.156c.728.54 1.607.853 2.553.853s1.825-.313 2.553-.853a.75.75 0 1 1 .894 1.205A5.77 5.77 0 0 1 12 17.75a5.77 5.77 0 0 1-3.447-1.148a.75.75 0 0 1-.155-1.049" clip-rule="evenodd"/>
                </svg>
                {{ apartment.title }} <span class="text-xs w-full text-gray-500"> {{apartment.locality}} - {{ apartment.rooms }} Chambres </span>
              </router-link>
              
              <!--
              <div class="flex flex-wrap gap-3 mb-3 text-sm text-gray-600">
                <span class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" class="mr-1">
                    <g fill="none" fill-rule="evenodd">
                      <path d="m12.594 23.258l-.012.002l-.071.035l-.02.004l-.014-.004l-.071-.036q-.016-.004-.024.006l-.004.01l-.017.428l.005.02l.01.013l.104.074l.015.004l.012-.004l.104-.074l.012-.016l.004-.017l-.017-.427q-.004-.016-.016-.018m.264-.113l-.014.002l-.184.093l-.01.01l-.003.011l.018.43l.005.012l.008.008l.201.092q.019.005.029-.008l.004-.014l-.034-.614q-.005-.019-.02-.022m-.715.002a.02.02 0 0 0-.027.006l-.006.014l-.034.614q.001.018.017.024l.015-.002l.201-.093l.01-.008l.003-.011l.018-.43l-.003-.012l-.01-.01z"/>
                      <path fill="currentColor" d="M3 5a1 1 0 0 1 .993.883L4 6v7h7V8a1 1 0 0 1 .883-.993L12 7h7a3 3 0 0 1 2.995 2.824L22 10v8a1 1 0 0 1-1.993.117L20 18v-3H4v3a1 1 0 0 1-1.993.117L2 18V6a1 1 0 0 1 1-1m4.5 2a2.5 2.5 0 1 1 0 5a2.5 2.5 0 0 1 0-5"/>
                    </g>
                  </svg>
                  {{ apartment.rooms }} Chambres
                </span>
                <span class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" class="mr-1">
                    <path fill="currentColor" d="M6 22q-.825 0-1.412-.587T4 20V4q0-.825.588-1.412T6 2h12q.825 0 1.413.588T20 4v16q0 .825-.587 1.413T18 22zm0-2h12V4H6zm1-2h10l-3.45-4.5l-2.3 3l-1.55-2zm-1 2V4z"/>
                  </svg>
                  {{ apartment.images.length }} Photos
                </span>
                <span class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" class="mr-1">
                    <path fill="currentColor" d="M10 5v5H9V5H5v8h4v-1h1v5H9v-3H5v5h7v-2h1v2h6v-2h2v4H3V3h18v12h-2v-5h-6v5h-1V9h7V5z"/>
                  </svg>
                  {{ apartment.floor || 'N/A' }}
                </span>
              </div>
              -->
              
              <div class="flex items-center">
                <span class="flex flex-wrap text-xs items-center">
                  <span class="text-xs font-bold flex items-center text-[#1f2937]">
                    {{ formatPrice(apartment.price) }} FCFA<span v-if="apartment?.category !== 'achat'" class="text-sm">/nuitée</span>
                  </span>
                </span>
                <!--
                <span class="text-xs font-bold text-[#1f2937]">
                  {{ formatPrice(apartment.price) }} <span class="text-sm">F/nuitée</span>
                  <span v-if="apartment.type === 'rent'" class="text-sm text-gray-500">/mois</span>
                </span>
                <router-link 
                  :to="`/apartments/${apartment._id}`" 
                  class="px-4 py-2 bg-[#D4AF37] text-gray-800 rounded-md font-semibold hover:bg-[#c5a030] transition-colors text-sm"
                >
                  Voir détails
                </router-link>
                -->
              </div>
            </div>
          </div>
        </div>

          <!-- Pagination -->
          <div class="flex justify-center mt-8" v-if="totalPages > 1">
            <nav class="inline-flex rounded-md shadow-sm -space-x-px flex-wrap gap-1" aria-label="Pagination">
              <!-- Bouton Précédent -->
              <button 
                @click="changePage(currentPage - 1)"
                :disabled="currentPage === 1"
                class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-xs font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Page précédente"
              >
                <svg class="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fill-rule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
              </button>

              <!-- Pages avec gestion des gaps -->
              <span 
                v-for="item in paginationRange"
                :key="`${item.type}-${item.page || 'dot'}`"
              >
                <span 
                  v-if="item.type === 'dot'"
                  class="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-xs font-medium text-gray-700"
                >
                  ...
                </span>
                <button 
                  v-else
                  @click="changePage(item.page)"
                  :class="{
                    'z-10 bg-[#D4AF37] border-[#D4AF37] text-gray-800 font-bold': currentPage === item.page,
                    'bg-white border-gray-300 text-gray-700 hover:bg-gray-50': currentPage !== item.page
                  }"
                  class="relative inline-flex items-center px-2 py-2 border text-xs font-medium"
                >
                  {{ item.page }}
                </button>
              </span>

              <!-- Bouton Suivant -->
              <button 
                @click="changePage(currentPage + 1)"
                :disabled="currentPage === totalPages"
                class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-xs font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Page suivante"
              >
                <svg class="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
              </button>
            </nav>
          </div>

        <div 
          v-if="loading && apartments.length > 0" 
          ref="loader"
          class="w-full flex items-center justify-center py-4"
        >
          <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#D4AF37]"></div>
        </div>
      </div>
    </div>
  </section>

  <FooterComponent/>
  </div>
</template>

<style scoped>
.degradeB {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to right, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5));
  pointer-events: none;
}

::-webkit-scrollbar {
  width: 6px;
}
::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.bounce-img {
  transition: transform 0.5s ease;
}

.bounce-img:hover {
  transform: scale(1.05);
}

input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  appearance: none;
  margin: 0;
}

input[type="number"] {
  -moz-appearance: textfield;
  appearance: textfield;
}
</style>
